export const PURCHASE_ORDER_AMOUNT_ROUNDING_MODES = ["line", "order"] as const;

export type PurchaseOrderAmountRoundingMode = (typeof PURCHASE_ORDER_AMOUNT_ROUNDING_MODES)[number];

export const DEFAULT_PURCHASE_ORDER_AMOUNT_ROUNDING_MODE: PurchaseOrderAmountRoundingMode = "line";

export const PURCHASE_ORDER_AMOUNT_ROUNDING_MODE_LABELS: Record<PurchaseOrderAmountRoundingMode, string> = {
  line: "Round each line",
  order: "Round the order total",
};

export function parsePurchaseOrderAmountRoundingMode(value: unknown): PurchaseOrderAmountRoundingMode {
  return String(value ?? "").trim().toLowerCase() === "order" ? "order" : "line";
}

export function purchaseOrderAmountRoundingModeLabel(mode: PurchaseOrderAmountRoundingMode): string {
  return PURCHASE_ORDER_AMOUNT_ROUNDING_MODE_LABELS[mode];
}

function moneyToCents(value: number): number {
  return Math.round(Number(value.toFixed(8)) * 100);
}

export function roundPurchaseOrderMoneyToCents(value: number): number {
  return moneyToCents(value) / 100;
}

export function purchaseOrderLineExtendedAmount(
  quantity: number,
  unitPrice: number,
  mode: PurchaseOrderAmountRoundingMode,
): number {
  const raw = Number((quantity * unitPrice).toFixed(8));
  if (mode === "line") {
    return roundPurchaseOrderMoneyToCents(raw);
  }
  return raw;
}

export function purchaseOrderOrderTotal(
  lines: Array<{ quantity: number; unitPrice: number }>,
  mode: PurchaseOrderAmountRoundingMode,
): number {
  if (mode === "line") {
    let cents = 0;
    for (const line of lines) {
      cents += moneyToCents(Number((line.quantity * line.unitPrice).toFixed(8)));
    }
    return cents / 100;
  }
  let raw = 0;
  for (const line of lines) {
    raw += Number((line.quantity * line.unitPrice).toFixed(8));
  }
  return roundPurchaseOrderMoneyToCents(Number(raw.toFixed(8)));
}

/** Header aggregate: round each qty × price, or round the summed order total. Null mode is line. */
export const SQL_PURCHASE_ORDER_ORDER_TOTAL = `coalesce(
  case coalesce(public.purchase_order.amount_rounding_mode, 'line')
    when 'order' then round(
      sum(public.purchase_order_item.quantity::numeric * coalesce(public.purchase_order_item.price, 0)),
      2
    )
    else sum(
      round(
        (public.purchase_order_item.quantity::numeric * coalesce(public.purchase_order_item.price, 0)),
        2
      )
    )
  end,
  0
)::double precision`;
